import { WSAlert, WSElement } from "@wingspanhq/fe-component-library";
import { useMemo } from "react";
import { useHistory } from "react-router-dom";
import { useQueryInternalAccounts } from "../../../query/banking/queries/useQueryInternalAccounts";
import { useUserId } from "../../../query/hooks/helpers";
import { useFeatureFlags } from "../../../query/hooks/useFeatureFlags";
import { useQueryCustomerEntity } from "../../../query/onboarding/queries/useQueryCustomerEntity";
import {
  useActivities,
  useMemberProfile,
  useUserProfile
} from "../../../query/users/queries";
import { useOnVisible } from "../../../shared/hooks/useOnVisible";
import { useWSAnalytics } from "../../../utils/WSAnalytics";
import { PATH_ONBOARDING_TAX_WITHHOLDINGS } from "../../Onboarding";
import { useModalDismissTaxWithholdingsSetup } from "../modals/ModalDismissTaxWithholdingsSetup";
import { useModalPauseReminders } from "../modals/ModalPauseReminders";
import { selectorProposeTaxWithholdings } from "../selectors/selectorProposeTaxWithholdings";

export const AlertEnableTaxWithholdings: React.FC = () => {
  const history = useHistory();
  const userId = useUserId();
  const queryUser = useUserProfile(userId);

  const queryMember = useMemberProfile(userId);
  const queryActivity = useActivities(userId);
  const queryMonitoredEntity = useQueryCustomerEntity();
  const queryFeatureFlags = useFeatureFlags();
  const queryInternalAccounts = useQueryInternalAccounts();

  const modalDismissTaxWithholdingsSetup =
    useModalDismissTaxWithholdingsSetup();
  const modalPauseReminders = useModalPauseReminders();

  const handleDismiss = () => {
    modalDismissTaxWithholdingsSetup.open({
      onPauseReminders: () => {
        modalPauseReminders.open();
      }
    });
  };

  const analytics = useWSAnalytics();

  const showTaxWithholdingAlert = useMemo(() => {
    if (
      !queryFeatureFlags.data ||
      !queryFeatureFlags.data.taxWithholdingInvoicingAlerts
    ) {
      return false;
    }

    if (
      !queryActivity.data ||
      queryActivity.data.events.taxWithholdingsAlertsDismissed
    ) {
      return false;
    }

    // If user has this field set, it means they already went though estimated tax rate flow, so we should show them AlertInvoiceTaxWithholdings
    if (queryMember.data?.profile.taxInfo?.incomeEstimateForYear) {
      return false;
    }

    return selectorProposeTaxWithholdings(
      queryUser.data,
      queryMember.data,
      queryMonitoredEntity.data,
      queryInternalAccounts.data
    );
  }, [
    queryFeatureFlags.data,
    queryActivity.data,
    queryMember.data,
    queryUser.data,
    queryMonitoredEntity.data,
    queryInternalAccounts.data
  ]);

  const [ref] = useOnVisible(() => {
    analytics.track.other("Invoicing Tax Withholdings Alert Viewed", {
      variant: "no_estimated_tax_rate"
    });
  });

  if (!showTaxWithholdingAlert) {
    return null;
  }

  return (
    <WSAlert
      theme="warning"
      icon="alert"
      title="You haven’t set up tax withholdings yet"
      action={{
        text: "Complete your setup",
        onClick: () => {
          analytics.track.other("Invoicing Tax Withholdings Alert Click", {
            variant: "no_estimated_tax_rate"
          });
          history.push(PATH_ONBOARDING_TAX_WITHHOLDINGS);
        }
      }}
      onDismiss={handleDismiss}
    >
      <WSElement ref={ref}></WSElement>
      As an independent contractor, you're considered self-employed by the IRS
      and responsible for income tax, self-employment tax, and quarterly
      payments. Avoid surprises at tax time by setting up your{" "}
      <b>tax withholdings</b> today to stay financially prepared.
    </WSAlert>
  );
};
